import React, { useState } from 'react';
import { X, UserPlus, Trash2, CheckCircle, Circle, ChevronDown } from 'lucide-react';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
}

interface AssignUsersModalProps {
    roleName: string; 
    assignedUsers: User[];
    availableUsers: User[]; // All system users
    onClose: () => void;
    onAssign: (userId: string) => void;
    onRemove: (userId: string) => void;
}

const AssignUsersModal: React.FC<AssignUsersModalProps> = ({
    roleName, 
    assignedUsers,
    availableUsers,
    onClose,
    onAssign,
    onRemove
}) => {
    const [selectedUserId, setSelectedUserId] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const unassignedUsers = availableUsers.filter(
        (u) => !assignedUsers.find((au) => au.id === u.id)
    );

    const filteredUsers = unassignedUsers.filter((user) =>
        `${user.firstName} ${user.lastName} ${user.email}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );
    const getFullName = (u: User) => `${u.firstName} ${u.lastName}`;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-[12px] font-sans">
            <div className="bg-neutral-900 text-white rounded-2xl shadow-2xl ring-1 ring-neutral-700 w-full max-w-2xl px-6 py-6 relative border border-neutral-800">
                {/* Close Button */}
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition">
                    <X className="w-4 h-4" />
                </button>

                {/* Header */}
                <h3 className="text-base font-semibold mb-5 flex items-center gap-2 text-white">
                    <UserPlus className="w-5 h-5 text-indigo-400" />
                    Assign Users to Role:
                    <span className="text-indigo-300 ml-1">{roleName}</span>
                </h3>

                {/* User Selection */}
                <div className="flex gap-2 items-center mb-6">
                    <div className="relative flex-1">
                        <div
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            className="bg-neutral-800 text-white text-sm rounded-md px-3 py-2 flex items-center justify-between cursor-pointer border border-neutral-700"
                        >
                            <span>
                                {selectedUserId
                                    ? getFullName(unassignedUsers.find((u) => u.id === selectedUserId)!) +
                                    ' - ' +
                                    unassignedUsers.find((u) => u.id === selectedUserId)!.email
                                    : 'Select user'}
                            </span>
                            <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                        </div>

                        {dropdownOpen && (
                            <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-neutral-900 border border-neutral-700 rounded-md shadow-xl">
                                <input
                                    autoFocus
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search users..."
                                    className="w-full bg-neutral-800 text-white px-3 py-2 text-sm border-b border-neutral-700 outline-none placeholder:text-gray-500"
                                />
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            onClick={() => {
                                                setSelectedUserId(user.id);
                                                setDropdownOpen(false);
                                                setSearchTerm('');
                                            }}
                                            className="px-3 py-2 hover:bg-neutral-700/50 text-white text-sm cursor-pointer"
                                        >
                                            {getFullName(user)} - {user.email}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-3 py-2 text-gray-500 text-sm">No users found.</div>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => {
                            if (selectedUserId) onAssign(selectedUserId);
                            setSelectedUserId('');
                        }}
                        disabled={!selectedUserId}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50"
                    >
                        Add to Role
                    </button>
                </div>

                {/* Assigned Users Table */}
                <div className="overflow-x-auto border border-neutral-700 rounded-lg">
                    <table className="w-full text-left text-sm text-gray-300">
                        <thead className="bg-neutral-950 text-gray-400 uppercase text-xs">
                            <tr>
                                <th className="px-3 py-2">Full Name</th>
                                <th className="px-3 py-2">Email</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-800">
                            {assignedUsers.map(user => (
                                <tr key={user.id} className="hover:bg-neutral-800/40 transition">
                                    <td className="px-3 py-2 text-white">{getFullName(user)}</td>
                                    <td className="px-3 py-2 text-gray-300">{user.email}</td>
                                    <td className="px-3 py-2">
                                        {user.isActive ? (
                                            <span className="flex items-center gap-1 text-green-400">
                                                <CheckCircle className="w-4 h-4" /> Active
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-gray-500">
                                                <Circle className="w-4 h-4" /> Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => setConfirmDeleteId(user.id)}
                                            className="text-gray-400 hover:text-red-500 transition"
                                            title="Unassign"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {assignedUsers.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-6 text-gray-500">
                                        No users assigned to this role.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="mt-6 text-right">
                    <button
                        onClick={onClose}
                        className="bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-md text-sm transition"
                    >
                        Close
                    </button>
                </div>

                {/* Confirm Dialog */}
                {confirmDeleteId && (
                    <ConfirmDialog
                        message="Are you sure you want to unassign this user from the role?"
                        onYes={() => {
                            onRemove(confirmDeleteId);
                            setConfirmDeleteId(null);
                        }}
                        onCancel={() => setConfirmDeleteId(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default AssignUsersModal;
